import './style.scss';
import { getLocalization } from 'global/global';
import { useAppSelector } from 'index';
import { CHART_DATA_FILTER } from 'Interfaces/ChartInterface';
import { FormInterface } from 'Interfaces/Forms/FormsInterface';
import { QuestionInterface, questionTypes } from 'Interfaces/Forms/QuestionInterface';
import * as React from 'react';
import { Button, ButtonGroup, Container, Form, ListGroup, ListGroupItem } from 'react-bootstrap';
import { uniqueFormsSelector } from 'reducers/formsReducer';
import RadarChartData from 'views/Charts/RadarChartLines/RadarChartData';
import FormUtils from 'views/SingleInstance/utils/FormUtils';
import { getFormUtils } from 'views/SingleInstance/utils/FormUtilsHolder';
import { isNumeric } from 'views/SingleInstance/utils/ScriptUtils';

interface Props {
  selectedForm: string;
  questions: string[];
  xAxis: string[];
  chartType: string;
  radarDataPoints: string[];
  dataFilter: CHART_DATA_FILTER;
  validate: boolean;
  useFilters: boolean;
  combineCharts: boolean;
  combineAndStack: boolean;
  stacking: string;
  setChartType: (type: string) => void;
  setStacking: (stacking: string) => void;
  setSelectedQuestion: (id: string, checked: boolean) => void;
  setXAxis: (id: string, checked: boolean) => void;
  reset: () => void;
  onDataPointSelected: (dataPoints: string[]) => void;
  onDataFilterSelected: (value) => void;
  onUseFiltersChange: (checked: boolean) => void;
  onCombineChartsChange: (checked: boolean) => void;
  onCombineAndStackChange: (checked: boolean) => void;
}

type ChartQuestions = Pick<QuestionInterface, 'id' | 'type' | 'text' | 'targetValue'>;

export default function ChartSelection(props: Props) {
  const forms: FormInterface[] = useAppSelector(state => uniqueFormsSelector(state));
  const locationLabels = useAppSelector(state => state.clientPersist.locationLabels);
  const { chartType } = props;
  const formUtils = React.useRef<FormUtils | null>(null);
  const [hasOptions, setHasOptions] = React.useState(true);
  const [questions, setQuestions] = React.useState<ChartQuestions[]>([]);
  const [stackQuestions, setStackQuestions] = React.useState<ChartQuestions[]>([]);
  const [xAxisQuestions, setXAxisQuestions] = React.useState<ChartQuestions[]>([]);
  const [formId, setFormId] = React.useState('');
  const [showRadar, setShowRadar] = React.useState(false);

  React.useEffect(() => {
    if (props.selectedForm !== formId) {
      setFormId(props.selectedForm);
      props.reset();
    }
  }, [props.selectedForm]);

  React.useEffect(() => {
    if (formId) {
      const f = forms.find(f => f.ref === props.selectedForm);
      if (f) {
        formUtils.current = getFormUtils(f);
        const qns = formUtils.current.getQuestions();
        const select = [
          'SelectOneQuestion',
          'SelectMultipleQuestion',
          'BooleanQuestion',
          'LikertScaleQuestion',
          'StatusQuestion'
        ];
        const keys = Object.keys(qns);
        let numericCount = 0;
        let ho = false;
        for (const key of keys) {
          if (!ho && select.includes(qns[key].type)) {
            ho = true;
          }
          if (isNumeric(qns[key]) && !qns[key].inVisible) {
            numericCount++;
          }
        }
        setHasOptions(ho);
        setShowRadar(numericCount > 2);
      }
    } else {
      props.reset();
    }
  }, [formId]);

  /* eslint-disable complexity */
  React.useEffect(() => {
    if (formUtils.current && chartType) {
      const allQuestions = formUtils.current.getQuestions();
      const keys = Object.keys(allQuestions);

      const list: ChartQuestions[] = [];
      const xAxis: ChartQuestions[] = [];
      const stack: ChartQuestions[] = [];

      const initXAxis = () => {
        if (chartType === 'BAR') {
          if (formUtils.current?.getModel().hasLocationHierarchy) {
            locationLabels.forEach((label, index) => {
              if (label) {
                xAxis.push({
                  type: questionTypes.INT_QUESTION,
                  text: label,
                  id: `location${index + 1}`,
                });
                stack.push({
                  type: questionTypes.INT_QUESTION,
                  text: label,
                  id: `location${index + 1}`,
                });
              }
            });
          }
          xAxis.push({
            type: questionTypes.INT_QUESTION,
            text: getLocalization('createdBy'),
            id: 'createdBy',
          });
          xAxis.push({
            type: questionTypes.INT_QUESTION,
            text: getLocalization('editor'),
            id: 'user_id',
          });
          stack.push({
            type: questionTypes.INT_QUESTION,
            text: getLocalization('createdBy'),
            id: 'createdBy',
          });
          stack.push({
            type: questionTypes.INT_QUESTION,
            text: getLocalization('editor'),
            id: 'user_id',
          });
        } else if (chartType === 'LINE') {
          xAxis.push({
            type: questionTypes.INT_QUESTION,
            text: getLocalization('createdOn'),
            id: 'created',
          });
          xAxis.push({
            type: questionTypes.INT_QUESTION,
            text: getLocalization('lastUpdatedAt'),
            id: 'modified',
          });
        }
      };
      initXAxis();
      const addNumericQuestions = (question: QuestionInterface) => {
        const isCount = question.id.toLowerCase() === 'countquestion';
        if (isCount || (
          (question.type !== questionTypes.CALCULATED_VALUE_QUESTION || question.numericOnly) && !question.inVisible)
        ) {
          if (chartType !== 'PIE') {
            if (isCount) {
              list.splice(0, 0, {
                type: question.type,
                text: question.text,
                id: question.id,
                targetValue: question.targetValue
              });
            } else {
              list.push({
                type: question.type,
                text: question.text,
                id: question.id,
                targetValue: question.targetValue
              });
            }
          }
        } else if (question.type === questionTypes.CALCULATED_VALUE_QUESTION && question.chartAsChoiceField) {
          addSelectQuestions(question);
        }
        if (chartType === 'BAR' && question.type === questionTypes.INT_QUESTION && !isCount) {
          xAxis.push({
            type: question.type,
            text: question.text,
            id: question.id,
            targetValue: question.targetValue
          });
        }
      };

      const addSelectQuestions = (question: QuestionInterface) => {
        if (chartType === 'LINE' || chartType === 'PIE') {
          list.push({
            type: question.type,
            text: question.text,
            id: question.id,
            targetValue: question.targetValue
          });
        }
        if (chartType === 'BAR') {
          xAxis.push({
            type: question.type,
            text: question.text,
            id: question.id,
            targetValue: question.targetValue
          });
          stack.push({
            type: question.type,
            text: question.text,
            id: question.id,
            targetValue: question.targetValue
          });
        }
      };

      const addChartStringQuestion = (question) => {
        if (question.validationlist && question.validationlist.length > 0) {
          if (chartType === 'BAR') {
            xAxis.push({
              type: question.type,
              text: question.text,
              id: question.id,
              targetValue: question.targetValue
            });
          }
        }
      };

      const addLocationHierarchyQuestions = () => {
        if (formUtils.current?.getModel().hasLocationHierarchy && chartType === 'PIE') {
          locationLabels.forEach((label, index) => {
            if (label) {
              list.push({
                type: questionTypes.INT_QUESTION,
                text: label,
                id: `location${index + 1}`,
              });
            }
          });
        }
      };

      for (const key of keys) {
        const question = allQuestions[key];
        if (question.inVisible && question.id.toLowerCase() !== 'countquestion') {
          continue;
        }
        if (question.id === 'countQuestion' && !question.text) {
          question.text = 'Count';
        }
        switch (question.type) {
          case 'IntQuestion':
          case 'FloatQuestion':
          case 'CalculatedValueQuestion':
            addNumericQuestions(question);
            break;
          case 'SelectOneQuestion':
          case 'SelectMultipleQuestion':
          case 'BooleanQuestion':
          case 'LikertScaleQuestion':
          case 'StatusQuestion':
            // these questions are available in all chart types.
            addSelectQuestions(question);
            break;
          case 'StringQuestion':
            addChartStringQuestion(question);
            break;
          case questionTypes.DATE_QUESTION:
          case questionTypes.TIME:
            if (chartType === 'LINE') {
              xAxis.push({
                type: question.type,
                text: question.text,
                id: question.id,
                targetValue: question.targetValue
              });
            }
            break;
          default:
            break;
        }
      }
      if (chartType === 'BAR') {
        xAxis.push({
          id: 'year-when-updated', text: getLocalization('yearUpdated'), type: questionTypes.INT_QUESTION
        });
        xAxis.push({
          id: 'year-when-created', text: getLocalization('yearCreated'), type: questionTypes.INT_QUESTION
        });
        xAxis.push({
          id: 'month-when-updated', text: getLocalization('monthUpdated'), type: questionTypes.INT_QUESTION
        });
        xAxis.push({
          id: 'month-when-created', text: getLocalization('monthCreated'), type: questionTypes.INT_QUESTION
        });
        xAxis.push({
          id: 'quarter-when-updated', text: getLocalization('quarterUpdated'), type: questionTypes.INT_QUESTION
        });
        xAxis.push({
          id: 'quarter-when-created', text: getLocalization('quarterCreated'), type: questionTypes.INT_QUESTION
        });
      }
      addLocationHierarchyQuestions();
      setQuestions(list);
      setXAxisQuestions(xAxis);
      setStackQuestions(stack);
      props.reset();
    }
  }, [chartType, formId]);

  const onQuestionSelect = (e) => {
    props.setSelectedQuestion(e.target.value, e.target.checked);
  };

  const onXAxisQuestionSelect = (e) => {
    props.setXAxis(e.target.value, e.target.checked);
    if (props.stacking === e.target.value) {
      props.setStacking('');
    }
  };

  const onStackQuestionSelected = (e) => {
    props.setStacking(e.target.checked ? e.target.value : '');
  };

  const onDataPointSelected = (value, checked) => {
    if (checked) {
      props.onDataPointSelected(props.radarDataPoints.concat([value]));
    } else {
      props.onDataPointSelected(props.radarDataPoints.filter(v => v !== value));
    }
  };

  const renderXAxisQuestions = React.useCallback(() => {
    return xAxisQuestions.map((question) => (
      <ListGroup.Item
        key={`chart-guide-${question.id}`}
        as="li"
        className="d-flex justify-content-between align-items-start"
      >
        <Form.Check
          id={`chart-x-axis-question-checkbox-${question.id}`}
          type={'checkbox'}
          label={question.text}
          onChange={onXAxisQuestionSelect}
          value={question.id}
          checked={props.xAxis.includes(question.id)}
          isInvalid={props.validate && props.xAxis.length === 0}
          className="ms-2 me-auto"
        />
      </ListGroup.Item>
    ));
  }, [chartType, xAxisQuestions, props.xAxis]);

  const renderQuestions = React.useCallback(() => {
    return questions.map((question) => question.text ? (
      <ListGroup.Item
        key={`chart-guide-${question.id}`}
        as="li"
        className="d-flex justify-content-between align-items-start"
      >
        <Form.Check
          id={`chart-question-checkbox-${question.id}`}
          type={'checkbox'}
          label={question.text}
          onChange={onQuestionSelect}
          value={question.id}
          checked={props.questions.includes(question.id)}
          disabled={chartType === 'RADAR' && (props.questions.length >= 16)}
          isInvalid={props.validate && (
            chartType === 'RADAR' ? props.questions.length < 3 : props.questions.length === 0
          )}
          className="ms-2 me-auto"
        />
      </ListGroup.Item>
    ) : null);
  }, [chartType, questions, props.questions]);

  const CombineCheckBox = React.useCallback(() => {
    return (
      <>
        {(chartType === 'LINE' || chartType === 'BAR') && (
          <Form.Check
            id={'chart-guide-combine-charts'}
            label={getLocalization('combineCharts')}
            checked={props.combineCharts}
            className="ms-2"
            inline={true}
            onChange={(e) => {
              props.onCombineAndStackChange(false);
              props.onCombineChartsChange(e.target.checked);
            }}
          />
        )}
      </>
    );
  }, [props.combineCharts, chartType]);

  return (
    <div>
      <Container className="mb-3">
        <h6>{getLocalization('selectcharttype')}</h6>
        <ButtonGroup aria-label="Basic example">
          <Button
            variant={chartType === 'BAR' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => props.setChartType('BAR')}
          >
            {getLocalization('barChart')}
          </Button>
          <Button
            variant={chartType === 'LINE' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => props.setChartType('LINE')}
          >
            {getLocalization('lineChart')}
          </Button>
          {hasOptions && (
            <Button
              variant={chartType === 'PIE' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => props.setChartType('PIE')}
            >
              {getLocalization('pieChart')}
            </Button>
          )}
          {showRadar && (
            <Button
              variant={chartType === 'RADAR' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => props.setChartType('RADAR')}
            >
              {getLocalization('radarChart')}
            </Button>
          )}
        </ButtonGroup>
        {!chartType && props.validate && (
          <Form.Text className="ms-2 danger-text">
            {getLocalization('chartTypeRequired')}
          </Form.Text>
        )}
        <Form.Check
          id={'chart-guide-use-filters'}
          label={getLocalization('useFilters')}
          checked={props.useFilters}
          className="ms-2"
          inline={true}
          onChange={(e) => props.onUseFiltersChange(e.target.checked)}
        />
        {CombineCheckBox()}
        {chartType === 'BAR' && (
          <Form.Check
            id={'chart-guide-combine-and-stack'}
            label={getLocalization('combineAndStack')}
            checked={props.combineAndStack}
            className="ms-2"
            inline={true}
            onChange={(e) => {
              props.onCombineChartsChange(false);
              props.onCombineAndStackChange(e.target.checked);
            }}
          />
        )}
      </Container>
      <Container className="mb-3">
        {chartType && (
          <h6>{getLocalization(
            chartType === 'BAR' ? 'barClassification' : 'selectQuestions'
          )}</h6>
        )}
        <ListGroup className="mt-2 form-question-selection-list">
          {chartType !== 'BAR' ? renderQuestions() : renderXAxisQuestions()}
        </ListGroup>
      </Container>
      {(chartType === 'BAR' || chartType === 'LINE') && (
        <Container className="mb-3">
          <h6>{getLocalization(chartType === 'LINE' ? 'selectXAxis' : 'barSize')}</h6>
          {chartType === 'BAR' && (
            <small>{getLocalization('barSizeInfo')}</small>
          )}
          <ListGroup className="form-question-selection-list mt-2">
            {chartType === 'LINE' ? renderXAxisQuestions() : renderQuestions()}
          </ListGroup>
        </Container>
      )}
      {(chartType === 'BAR') && (
        <Container className="mb-3">
          <h6>{getLocalization('stackingCriteria')}</h6>
          <ListGroup className="form-question-selection-list mt-2">
            {stackQuestions.map((question) => {
              if (props.xAxis.includes(question.id)) {
                return null;
              }
              return (
                <ListGroup.Item
                  key={`chart-stacking-guide-${question.id}`}
                  as="li"
                  className="d-flex justify-content-between align-items-start"
                >
                  <Form.Check
                    id={`chart-stack-question-checkbox-${question.id}`}
                    type={'checkbox'}
                    label={question.text}
                    onChange={onStackQuestionSelected}
                    value={question.id}
                    checked={props.stacking === question.id}
                    className="ms-2 me-auto"
                  />
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Container>
      )}
      {chartType === 'RADAR' ? (
        <Container className="mb-3">
          <h6>{getLocalization('addLines')}</h6>
          <RadarChartData
            formId={props.selectedForm}
            dataFilter={props.dataFilter}
            selected={props.radarDataPoints}
            ListItem={ListGroupItem}
            validate={props.validate}
            questions={questions.map(q => q.id)}
            onDataPointSelected={onDataPointSelected}
            onDataFilterSelected={(value) => props.onDataFilterSelected(value)}
          />
        </Container>
      ) : null}
    </div>
  );
}
